import React from "react";

import { ANNOTATION_COLORS } from "@perceptron/constants/colors";
import { AnnotationSource } from "@perceptron/core/types";
import { cn } from "@perceptron/utils/cn";
import { decode } from "he";

import { useAnnotationInteractions } from "@demo/hooks/annotations/useAnnotationInteractions";

interface BasePointAnnotationProps {
  id: string;
  source: AnnotationSource;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  showLabel?: boolean;
  labelText?: string;
}

export const BasePointAnnotation = React.memo(function BasePointAnnotation({
  id,
  source,
  children,
  className = "",
  style,
  showLabel = true,
  labelText = `Point ${id}`,
}: BasePointAnnotationProps) {
  const { isHighlighted, clickedAnnotationId, handleMouseEnter, handleMouseLeave, handleClick } =
    useAnnotationInteractions();

  const isClicked = clickedAnnotationId === id;
  const shouldShowLabel = showLabel && (isHighlighted(id) || isClicked);

  return (
    <span
      className={cn("cursor-pointer transition-all duration-200", className)}
      data-testid={`point-marker-${id}`}
      id={`point-marker-${id}`}
      style={style}
      onClick={() => handleClick(id, source)}
      onMouseEnter={() => handleMouseEnter([id], source)}
      onMouseLeave={() => handleMouseLeave(id)}
    >
      {children}
      {shouldShowLabel && (
        <div
          className="absolute -top-8 left-1/2 z-10 -translate-x-1/2 transform whitespace-nowrap rounded-sm px-2 py-0.5 text-xs text-white"
          style={{ backgroundColor: ANNOTATION_COLORS.PRIMARY }}
        >
          {decode(labelText)}
        </div>
      )}
    </span>
  );
});
